import {
  Card as MuiCard,
  CardProps as MuiCardProps,
  CardContent as MuiCardContent,
  CardContentProps as MuiCardContentProps,
  styled,
} from '@mui/material';
import { FC, forwardRef, KeyboardEvent } from 'react';

export type CardProps = MuiCardProps & { disabled?: boolean };

const StyledCard = styled(MuiCard, {
  shouldForwardProp: (prop) => prop !== 'disabled',
})<{ disabled?: boolean; onClick?: CardProps['onClick'] }>(
  ({ theme, disabled, onClick }) => ({
    ...(onClick && {
      cursor: disabled ? 'not-allowed' : 'pointer',
      '&:hover': {
        backgroundColor: theme.palette.grey[50],
      },
    }),
  }),
);

export const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ onClick, disabled, onKeyDown, ...props }, ref) => {
    const isInteractive = !!onClick;

    const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
      if (isInteractive && !disabled && (e.key === 'Enter' || e.key === ' ')) {
        e.preventDefault();
        e.currentTarget.click();
      }
      onKeyDown?.(e);
    };

    return (
      <StyledCard
        ref={ref}
        onClick={disabled ? undefined : onClick}
        disabled={disabled}
        onKeyDown={isInteractive ? handleKeyDown : onKeyDown}
        role={isInteractive ? 'button' : undefined}
        tabIndex={isInteractive && !disabled ? 0 : undefined}
        aria-disabled={isInteractive && disabled ? true : undefined}
        {...props}
      />
    );
  },
);

type CardContentSize = 'xs' | 'sm' | 'md';

export type CardContentProps = MuiCardContentProps & {
  size?: CardContentSize;
};

const StyledCardContent = styled(MuiCardContent, {
  shouldForwardProp: (prop) => prop !== 'size',
})<{ size: CardContentSize }>(({ theme, size }) => ({
  ...(size === 'xs' && {
    padding: theme.spacing(theme.spacingSizes.xs),
    '&:last-child': {
      paddingBottom: theme.spacing(theme.spacingSizes.xs),
    },
  }),
  ...(size === 'sm' && {
    padding: theme.spacing(theme.spacingSizes.sm),
    '&:last-child': {
      paddingBottom: theme.spacing(theme.spacingSizes.sm),
    },
  }),
}));

export const CardContent = forwardRef<HTMLDivElement, CardContentProps>(
  ({ size = 'md', ...props }, ref) => (
    <StyledCardContent ref={ref} size={size} {...props} />
  ),
) as FC<CardContentProps>;
